<template>
	<page-container :page="page">
		<heading-image
			v-if="page && page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content
			:title="page.title || page.header"
			:title-right="page.titleRight"
			:subtitle="page.subtitle"
			:content="page.content"
			:intro-image="page.introImage"
			:intro-image-webp="page.introImageWebp"
			:intro-image-portrait="page.introImagePortrait"
			:intro-image-portrait-webp="page.introImagePortraitWebp"
		/>

		<section class="gallery-section main-content background-dark">
			<filter-buttons :items="categories" :filter="filter" @change-filter="filter = $event" />
			<image-grid :items="galleryFiltered" :filter="filter" @change-filter="filter = $event" />
		</section>

		<contentblocks v-if="page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>

		<newsletter
			v-if="page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const filter = ref('showAll');

const { data: gallery } = await useWebsiteFetch('gallery', {
	query: { language: locale.value },
	key: `${locale.value}/gallery`,
});

const categories = [
	...new Set(
		gallery.value
			.filter((item) => item.galleryCategorie)
			.map((item) => item.galleryCategorie && item.galleryCategorie.header),
	),
];

const galleryFiltered = computed(() => {
	if (filter.value === 'showAll') {
		return gallery.value;
	}

	return [...gallery.value].filter((item) => {
		if (!item.galleryCategorie) {
			return false;
		}

		return item.galleryCategorie.header === filter.value;
	});
});

onMounted(() => {
	Fancybox.bind('[data-fancybox]', {});
});
</script>

<style lang="scss" scoped>
.main-content {
	padding: 25px 0;
}

.gallery-section img {
	width: 100%;
	padding: 30px 0;
	cursor: pointer;
}

.video-play {
	background: url('~/assets/images/video-play-button.png') no-repeat center center;
	background-size: 40px;
	position: absolute;
	top: 50%;
	left: 50%;
	z-index: 3;
	width: 40px;
	height: 40px;
	margin: -20px 0 0 -20px;
	cursor: pointer;
	transition: all 0.3s ease-in-out;
}

.gallery-item {
	position: relative;

	&:hover {
		.video-play {
			transform: scale(1.05);
		}
	}
}

.gallery-items {
	margin: 50px auto;
	font-size: 0;
	text-align: left;
	display: flex;
	flex-flow: row wrap;

	.column3 {
		float: none;
		margin: 0 0 25px;
		width: 25%;
		vertical-align: top;
		position: relative;

		&:hover span {
			opacity: 1;
		}
	}

	span {
		font-size: 1rem;
		opacity: 0;
		position: absolute;
		left: 30px;
		right: 30px;
		bottom: 30px;
		padding: 25px;
		transition: all 0.3s ease-in-out;
		color: #fff;
		background: rgb(0 0 0 / 40%);
	}

	.gallery-title i {
		margin: 0 5px 0 0;
	}
}

@media (max-width: 960px) {
	.gallery-items .column3 {
		width: 50%;
		max-width: 600px;
		margin: 0;
		padding: 0 10px;
	}
}

@media (max-width: 900px) {
	.items .filter-item.active {
		width: 50%;
	}
}
</style>
